import React, { useState, useEffect } from 'react';
import { 
    Box, Grid, Card, CardContent, Typography, Chip, Dialog, DialogTitle, 
    DialogContent, DialogActions, Button, FormControl, InputLabel, Select, 
    MenuItem, Divider, Tabs, Tab, TextField, Checkbox, FormControlLabel,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper
} from '@mui/material';
import TableBarIcon from '@mui/icons-material/TableBar';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import EventSeatIcon from '@mui/icons-material/EventSeat';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import IconButton from '@mui/material/IconButton';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import PaymentsIcon from '@mui/icons-material/Payments';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CancelIcon from '@mui/icons-material/Cancel';
import TakeoutDiningIcon from '@mui/icons-material/TakeoutDining';
import AddIcon from '@mui/icons-material/Add';

import { useApp } from '../context/AppContext';
import { useLanguage } from '../context/LanguageContext';
import { printElement, BluetoothPrinterManager, printOrderReceipt } from '../utils/printHelper';

// Helper to format Date and Time to Indian Standard Time (IST)
const formatDateTimeIST = (dateInput) => {
    if (!dateInput) return '-';
    try {
        let d;
        if (typeof dateInput === 'string') {
            const iso = dateInput.replace(' ', 'T');
            d = new Date(iso);
        } else {
            d = new Date(dateInput);
        }
        if (isNaN(d.getTime())) return String(dateInput);

        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        const dateStr = `${day}-${month}-${year}`;

        const timeStr = d.toLocaleTimeString('en-IN', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });

        return `${dateStr} ${timeStr}`;
    } catch (e) {
        return String(dateInput);
    }
};

export default function TablesView() {
    const { 
        tables, 
        setTables,
        sections, 
        orders, 
        selectTable, 
        startNewParcelOrder,
        splitTable, 
        mergeSubTables, 
        shiftTable,
        settleBill,
        cancelOrder,
        bulkClearTables,
        fetchFreshTablesAndOrders,
        showToast,
        pinnedTables = [],
        togglePinTable,
        currentUser,
        getActiveBranchId,
        setActiveView,
        tableCarts = {}
    } = useApp();
    const { currentLanguage } = useLanguage();

    const isMr = currentLanguage === 'mr';
    const isHi = currentLanguage === 'hi';
    const isOwnerOrAdmin = currentUser?.role === 'owner' || currentUser?.role === 'admin' || currentUser?.is_owner || localStorage.getItem('dinexa_user_role') === 'owner' || localStorage.getItem('dinexa_user_role') === 'admin';

    // State for Actions & Dialogs
    const [selectedTable, setSelectedTable] = useState(null);
    const [actionDialogOpen, setActionDialogOpen] = useState(false);
    const [splitCount, setSplitCount] = useState(2);
    const [isSplitMode, setIsSplitMode] = useState(false);
    const [targetShiftTableId, setTargetShiftTableId] = useState('');
    const [isRefreshing, setIsRefreshing] = useState(false);

    // Live Checkout Modal States
    const [checkoutModalOpen, setCheckoutModalOpen] = useState(false);
    const [checkoutTable, setCheckoutTable] = useState(null);
    const [checkoutDiscount, setCheckoutDiscount] = useState(0);
    const [checkoutPaymentMethod, setCheckoutPaymentMethod] = useState('cash');
    const [checkoutSplitCash, setCheckoutSplitCash] = useState(0);
    const [checkoutSplitUpi, setCheckoutSplitUpi] = useState(0);

    // Filter chip for Active Counter/Delivery Orders bar
    const [counterChannelFilter, setCounterChannelFilter] = useState('all');

    // Bulk Clear Modal States
    const [bulkClearModalOpen, setBulkClearModalOpen] = useState(false);
    const [selectedTableIdsForClear, setSelectedTableIdsForClear] = useState([]);

    // Cancel Order Modal State
    const [cancelModalOpen, setCancelModalOpen] = useState(false);
    const [cancelReason, setCancelReason] = useState('Customer Left / Cancelled');

    const [activeSectionTab, setActiveSectionTab] = useState(() => {
        if (sections && Array.isArray(sections) && sections.length > 0) {
            const sorted = [...sections].sort((a, b) => (parseInt(a.display_order) || 0) - (parseInt(b.display_order) || 0));
            return sorted[0].id;
        }
        return 'all';
    });


    // On-Demand Fresh Database Sync on initial load
    useEffect(() => {
        if (navigator.onLine && typeof fetchFreshTablesAndOrders === 'function') {
            fetchFreshTablesAndOrders(true).catch(err => console.warn("On-demand initial sync error:", err));
        }
    }, []);

    // Manual Refresh handler
    const handleManualRefresh = async () => {
        setIsRefreshing(true);
        try {
            if (typeof fetchFreshTablesAndOrders === 'function') {
                await fetchFreshTablesAndOrders(true);
            }
            showToast(isMr ? 'टेबल्स व ऑर्डर्स रीफ्रेश झाले!' : 'Tables & Orders refreshed!');
        } catch (e) {
            showToast(isMr ? 'रीफ्रेश करताना अडचण आली.' : 'Failed to refresh.');
        } finally {
            setIsRefreshing(false);
        }
    };

    // Auto-select first section if currently on 'all' and sections exist
    useEffect(() => {
        if (sections && Array.isArray(sections) && sections.length > 0) {
            const hasMatch = sections.some(s => String(s.id) === String(activeSectionTab));
            if (!hasMatch || activeSectionTab === 'all') {
                const sorted = [...sections].sort((a, b) => (parseInt(a.display_order) || 0) - (parseInt(b.display_order) || 0));
                setActiveSectionTab(sorted[0].id);
            }
        }
    }, [sections, activeSectionTab]);

    // Filter dining tables & Auto-heal ghost status on the fly with robust multi-key matching
    const diningTables = (tables || [])
        .filter(t => t.id !== 'counter' && t.status !== 'split')
        .map(t => {
            let order = t.activeOrderId 
                ? (orders || []).find(o => String(o.id) === String(t.activeOrderId) || String(o.order_id) === String(t.activeOrderId)) 
                : null;

            // Fallback lookup: find any active unserved order matching this table's ID or Number
            let activeOrder = (order && !['served', 'cancelled'].includes(String(order.status).toLowerCase())) ? order : null;
            if (!activeOrder) {
                const isNumericOnly = /^\d+$/.test(String(t.number || t.id).trim());
                const cleanTNum = isNumericOnly ? String(t.number || t.id).replace(/[^0-9]/g, '') : null;
                activeOrder = (orders || []).find(o => {
                    if (['served', 'cancelled'].includes(String(o.status).toLowerCase())) return false;
                    const oTable = String(o.table_id || o.tableId || '').trim();
                    if (oTable === String(t.id).trim() || (t.number && oTable === String(t.number).trim())) return true;
                    if (cleanTNum && /^\d+$/.test(oTable) && oTable.replace(/[^0-9]/g, '') === cleanTNum) return true;
                    return false;
                }) || null;
            }

            const hasActiveOrder = activeOrder && Array.isArray(activeOrder.items) && activeOrder.items.length > 0;
            const hasDraftItems = Array.isArray(tableCarts && tableCarts[t.id]) && tableCarts[t.id].length > 0;

            if (hasActiveOrder) {
                return {
                    ...t,
                    status: activeOrder.status === 'billed' ? 'billed' : 'occupied',
                    activeOrderId: activeOrder.id
                };
            }

            if (hasDraftItems) {
                return {
                    ...t,
                    status: 'occupied',
                    activeOrderId: t.activeOrderId || null
                };
            }

            // If table has NO active unserved order and NO draft items, and its status was occupied or billed:
            // Auto-heal it to vacant so settled/cleared tables never get stuck as occupied!
            if (t.status === 'occupied' || t.status === 'billed') {
                return { ...t, status: 'vacant', activeOrderId: null };
            }
            return t;
        });

    const displayedTables = diningTables
        .filter(t => {
            if (!sections || sections.length === 0 || activeSectionTab === 'all') return true;
            return String(t.section_id) === String(activeSectionTab);
        })
        .sort((a, b) => {
            const isPinArray = Array.isArray(pinnedTables);
            const aPinned = isPinArray && pinnedTables.includes(tableKey(a.id));
            const bPinned = isPinArray && pinnedTables.includes(tableKey(b.id));
            if (aPinned && !bPinned) return -1;
            if (!aPinned && bPinned) return 1;
            const orderA = parseInt(a.display_order) || 0;
            const orderB = parseInt(b.display_order) || 0;
            return orderA - orderB;
        });

    // Helper for table key
    function tableKey(id) {
        return id;
    }

    // Real-time Summary Counters
    const totalCount = diningTables.length;
    const occupiedCount = diningTables.filter(t => t.status === 'occupied').length;
    const billedCount = diningTables.filter(t => t.status === 'billed').length;
    const dirtyCount = diningTables.filter(t => t.status === 'dirty' || t.status === 'cleaning').length;
    const reservedCount = diningTables.filter(t => t.status === 'reserved').length;
    const vacantCount = Math.max(0, totalCount - (occupiedCount + billedCount + dirtyCount + reservedCount));

    const getStatusColor = (status) => {
        if (status === 'occupied') return '#ef4444'; // Red
        if (status === 'billed') return '#f59e0b'; // Yellow
        if (status === 'dirty' || status === 'cleaning') return '#8b5cf6'; // Purple / Violet
        if (status === 'reserved') return '#3b82f6'; // Blue
        if (status === 'split') return '#64748b'; // Gray
        return '#10b981'; // Green (vacant)
    };

    const getStatusBg = (status) => {
        if (status === 'occupied') return '#fef2f2';
        if (status === 'billed') return '#fffbeb';
        if (status === 'dirty' || status === 'cleaning') return '#f5f3ff';
        if (status === 'reserved') return '#eff6ff';
        if (status === 'split') return '#f1f5f9';
        return '#f0fdf4';
    };

    const getStatusLabel = (status) => {
        if (status === 'occupied') return isMr ? '🔴 ऑर्डर चालू' : (isHi ? '🔴 ऑर्डर चालू' : '🔴 OCCUPIED');
        if (status === 'billed') return isMr ? '🟡 बिल तयार' : (isHi ? '🟡 बिल तैयार' : '🟡 BILLED');
        if (status === 'dirty' || status === 'cleaning') return isMr ? '🧹 साफ करणे बाकी' : (isHi ? '🧹 सफाई बाकी' : '🧹 CLEANING');
        if (status === 'reserved') return isMr ? '🔵 आरक्षित' : (isHi ? '🔵 आरक्षित' : '🔵 RESERVED');
        if (status === 'split') return 'SPLIT';
        return isMr ? '🟢 मोकळा' : (isHi ? '🟢 खाली' : '🟢 VACANT');
    };

    const handleCardClick = (table) => {
        setSelectedTable(table);
        setIsSplitMode(false);
        setTargetShiftTableId('');
        setActionDialogOpen(true);
        if (navigator.onLine && typeof fetchFreshTablesAndOrders === 'function') {
            fetchFreshTablesAndOrders(true).catch(err => console.warn("On-demand fresh table sync:", err));
        }
    };

    // Open Instant Live Checkout Modal
    const handleOpenCheckout = (table) => {
        setActionDialogOpen(false);
        setCheckoutTable(table);
        setCheckoutDiscount(0);

        const cleanTNum = String(table?.number || table?.id || '').replace(/[^0-9]/g, '');
        const activeOrder = table?.activeOrderId 
            ? (orders || []).find(o => String(o.id) === String(table.activeOrderId)) 
            : (orders || []).find(o => {
                if (['served', 'cancelled'].includes(String(o.status).toLowerCase())) return false;
                const oTable = String(o.table_id || o.tableId || '');
                const oClean = oTable.replace(/[^0-9]/g, '');
                return oTable === String(table?.id) || oTable === String(table?.number) || (cleanTNum && oClean === cleanTNum);
            });

        const isZomato = activeOrder && (activeOrder.order_source === 'zomato' || activeOrder.orderSource === 'zomato' || (activeOrder.type && activeOrder.type.toLowerCase().includes('zomato')));
        const isSwiggy = activeOrder && (activeOrder.order_source === 'swiggy' || activeOrder.orderSource === 'swiggy' || (activeOrder.type && activeOrder.type.toLowerCase().includes('swiggy')));

        if (isZomato) {
            setCheckoutPaymentMethod('zomato');
        } else if (isSwiggy) {
            setCheckoutPaymentMethod('swiggy');
        } else {
            setCheckoutPaymentMethod('cash');
        }

        const total = activeOrder ? Number(activeOrder.total || 0) : 0;
        setCheckoutSplitCash(total);
        setCheckoutSplitUpi(0);
        setCheckoutModalOpen(true);
        if (navigator.onLine && typeof fetchFreshTablesAndOrders === 'function') {
            fetchFreshTablesAndOrders(true).catch(err => console.warn("On-demand fresh checkout sync:", err));
        }
    };

    // Action Handlers
    const handleStartOrder = () => {
        if (selectedTable) {
            setActionDialogOpen(false);
            selectTable(selectedTable.id);
        }
    };

    const handleUpdateStatus = (newStatus) => {
        if (!selectedTable) return;
        setTables(prev => (prev || []).map(t => {
            if (t.id === selectedTable.id) {
                return { ...t, status: newStatus, activeOrderId: newStatus === 'vacant' ? null : t.activeOrderId };
            }
            return t;
        }));
        if (newStatus === 'vacant' && typeof bulkClearTables === 'function') {
            bulkClearTables([selectedTable.id]).catch(err => console.warn("Clear single table sync:", err));
        }
        showToast(
            newStatus === 'reserved' 
                ? `टेबल ${selectedTable.number} आरक्षित (Reserved) केला!` 
                : (newStatus === 'dirty' ? `टेबल ${selectedTable.number} साफ करणे बाकी!` : `टेबल ${selectedTable.number} मोकळा (Vacant) केला!`)
        );
        setActionDialogOpen(false);
    };

    const handleConfirmShift = () => {
        if (selectedTable && targetShiftTableId) {
            shiftTable(selectedTable.id, targetShiftTableId);
            setActionDialogOpen(false);
        }
    };

    const handleConfirmSplit = () => {
        if (selectedTable) {
            splitTable(selectedTable.id, splitCount);
            setActionDialogOpen(false);
        }
    };

    // Confirm Settlement from Modal
    const handleConfirmSettle = async (shouldPrint = false) => {
        if (!checkoutTable) return;
        const cleanTNum = String(checkoutTable.number || checkoutTable.id || '').replace(/[^0-9]/g, '');
        const activeOrder = checkoutTable.activeOrderId 
            ? (orders || []).find(o => String(o.id) === String(checkoutTable.activeOrderId)) 
            : (orders || []).find(o => {
                if (['served', 'cancelled'].includes(String(o.status).toLowerCase())) return false;
                const oTable = String(o.table_id || o.tableId || '');
                const oClean = oTable.replace(/[^0-9]/g, '');
                return oTable === String(checkoutTable.id) || oTable === String(checkoutTable.number) || (cleanTNum && oClean === cleanTNum);
            });

        if (!activeOrder) {
            showToast("⚠️ या टेबलची ऑर्डर सापडली नाही.");
            setCheckoutModalOpen(false);
            return;
        }

        const discountVal = parseFloat(checkoutDiscount) || 0;
        const finalPaymentMethod = checkoutPaymentMethod === 'split' 
            ? `Split (Cash: ₹${checkoutSplitCash}, UPI: ₹${checkoutSplitUpi})`
            : checkoutPaymentMethod;
        
        // Print Receipt if requested
        if (shouldPrint) {
            const connectionType = localStorage.getItem('dinexa_printer_connection_type') || 'system';
            if (connectionType !== 'system') {
                try {
                    await BluetoothPrinterManager.printReceiptCanvas({
                        ...activeOrder,
                        discount: discountVal,
                        paymentMethod: finalPaymentMethod
                    }, currentLanguage);
                } catch (e) {
                    printOrderReceipt({
                        ...activeOrder,
                        discount: discountVal,
                        paymentMethod: finalPaymentMethod
                    }, currentLanguage);
                }
            } else {
                printOrderReceipt({
                    ...activeOrder,
                    discount: discountVal,
                    paymentMethod: finalPaymentMethod
                }, currentLanguage);
            }
        }

        // Perform settlement in AppContext & Database - cleanly passing actual Table ID & Order ID
        if (typeof settleBill === 'function') {
            settleBill(discountVal, 0, finalPaymentMethod, '', activeOrder.order_source || activeOrder.orderSource || 'dine_in', checkoutTable.id, activeOrder.id);
        }

        setCheckoutModalOpen(false);
        setCheckoutTable(null);
    };

    // Open Bulk Clear Modal
    const handleOpenBulkClear = () => {
        const pendingTables = diningTables.filter(t => t.status === 'occupied' || t.status === 'billed');
        setSelectedTableIdsForClear(pendingTables.map(t => t.id));
        setBulkClearModalOpen(true);
    };

    // Execute Bulk Clear
    const handleExecuteBulkClear = async () => {
        if (selectedTableIdsForClear.length === 0) {
            showToast("कृपया कमीत कमी एक टेबल निवडा!");
            return;
        }
        if (typeof bulkClearTables === 'function') {
            await bulkClearTables(selectedTableIdsForClear);
        }
        setBulkClearModalOpen(false);
    };

    // Get Active Order for Settle Modal
    const cleanCheckoutTNum = String(checkoutTable?.number || checkoutTable?.id || '').replace(/[^0-9]/g, '');
    const currentCheckoutOrder = checkoutTable?.activeOrderId 
        ? (orders || []).find(o => String(o.id) === String(checkoutTable.activeOrderId)) 
        : (orders || []).find(o => {
            if (['served', 'cancelled'].includes(String(o.status).toLowerCase())) return false;
            const oTable = String(o.table_id || o.tableId || '');
            const oClean = oTable.replace(/[^0-9]/g, '');
            return oTable === String(checkoutTable?.id) || oTable === String(checkoutTable?.number) || (cleanCheckoutTNum && oClean === cleanCheckoutTNum);
        });
    const enableGst = localStorage.getItem('dinexa_gst_enabled') === 'true' || localStorage.getItem('dinexa_gst_enabled') === '1';
    const checkoutSubtotal = currentCheckoutOrder ? (currentCheckoutOrder.items || []).reduce((sum, item) => sum + (Number(item.price) * (Number(item.quantity) || 1)), 0) : 0;
    const checkoutTax = enableGst ? (checkoutSubtotal * 0.05) : 0;
    const checkoutDiscountAmount = parseFloat(checkoutDiscount) || 0;
    const checkoutGrandTotal = Math.max(0, checkoutSubtotal + checkoutTax - checkoutDiscountAmount);

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: '#f8fafc', minHeight: 'calc(100vh - 70px)' }}>
            
            {/* Top Toolbar: Counters + Actions */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5, flexWrap: 'wrap', gap: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography variant="h5" sx={{ fontWeight: 900, color: '#0f172a' }}>
                        🍽️ {isMr ? 'टेबल लेआउट (Floor Tables)' : 'Dining Floor Tables'}
                    </Typography>
                </Box>

                {/* Right Quick Action Buttons */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                    {/* Manual Refresh Button */}
                    <Button
                        variant="outlined"
                        size="medium"
                        startIcon={<RefreshIcon sx={{ animation: isRefreshing ? 'spin 1s linear infinite' : 'none', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />}
                        onClick={handleManualRefresh}
                        disabled={isRefreshing}
                        sx={{ 
                            fontWeight: 800, 
                            borderColor: '#cbd5e1', 
                            color: '#334155',
                            bgcolor: '#ffffff',
                            borderRadius: 2.5,
                            textTransform: 'none',
                            '&:hover': { bgcolor: '#f1f5f9', borderColor: '#94a3b8' }
                        }}
                    >
                        {isMr ? 'रिफ्रेश (Sync)' : 'Refresh'}
                    </Button>

                    {/* Owner/Manager Bulk Reset Tool */}
                    {(currentUser?.role === 'owner' || currentUser?.role === 'manager' || !currentUser?.role) && (
                        <Button
                            variant="contained"
                            size="medium"
                            startIcon={<DeleteSweepIcon />}
                            onClick={handleOpenBulkClear}
                            sx={{ 
                                fontWeight: 800, 
                                bgcolor: '#dc2626', 
                                color: '#ffffff',
                                borderRadius: 2.5,
                                textTransform: 'none',
                                '&:hover': { bgcolor: '#b91c1c' }
                            }}
                        >
                            🧹 {isMr ? 'पेंडिंग टेबल्स रिसेट करा' : 'Clear Occupied Tables'}
                        </Button>
                    )}
                </Box>
            </Box>

            {/* Real-time Summary Cards Top Banner (5 Dynamic Cards) */}
            <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
                <Grid item xs={6} sm={2.4}>
                    <Card sx={{ bgcolor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 3, boxShadow: 'none' }}>
                        <CardContent sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 800, fontSize: '0.75rem' }}>
                                    {isMr ? 'एकूण टेबल्स' : 'TOTAL TABLES'}
                                </Typography>
                                <Typography variant="h5" sx={{ fontWeight: 900, color: '#0f172a' }}>
                                    {totalCount}
                                </Typography>
                            </Box>
                            <TableBarIcon sx={{ color: '#64748b', fontSize: 28 }} />
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={6} sm={2.4}>
                    <Card sx={{ bgcolor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 3, boxShadow: 'none' }}>
                        <CardContent sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="caption" sx={{ color: '#15803d', fontWeight: 800, fontSize: '0.75rem' }}>
                                    {isMr ? '🟢 मोकळे (VACANT)' : '🟢 VACANT'}
                                </Typography>
                                <Typography variant="h5" sx={{ fontWeight: 900, color: '#16a34a' }}>
                                    {vacantCount}
                                </Typography>
                            </Box>
                            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#16a34a' }} />
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={6} sm={2.4}>
                    <Card sx={{ bgcolor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 3, boxShadow: 'none' }}>
                        <CardContent sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="caption" sx={{ color: '#b91c1c', fontWeight: 800, fontSize: '0.75rem' }}>
                                    {isMr ? '🔴 चालू (OCCUPIED)' : '🔴 OCCUPIED'}
                                </Typography>
                                <Typography variant="h5" sx={{ fontWeight: 900, color: '#dc2626' }}>
                                    {occupiedCount}
                                </Typography>
                            </Box>
                            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#dc2626' }} />
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={6} sm={2.4}>
                    <Card sx={{ bgcolor: '#fffbeb', border: '1px solid #fde68a', borderRadius: 3, boxShadow: 'none' }}>
                        <CardContent sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="caption" sx={{ color: '#b45309', fontWeight: 800, fontSize: '0.75rem' }}>
                                    {isMr ? '🟡 बिल तयार (BILLED)' : '🟡 BILLED'}
                                </Typography>
                                <Typography variant="h5" sx={{ fontWeight: 900, color: '#d97706' }}>
                                    {billedCount}
                                </Typography>
                            </Box>
                            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#d97706' }} />
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={6} sm={2.4}>
                    <Card sx={{ bgcolor: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 3, boxShadow: 'none' }}>
                        <CardContent sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="caption" sx={{ color: '#6d28d9', fontWeight: 800, fontSize: '0.75rem' }}>
                                    {isMr ? '🧹 सफाई बाकी' : '🧹 CLEANING'}
                                </Typography>
                                <Typography variant="h5" sx={{ fontWeight: 900, color: '#7c3aed' }}>
                                    {dirtyCount}
                                </Typography>
                            </Box>
                            <CleaningServicesIcon sx={{ color: '#7c3aed', fontSize: 24 }} />
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* ACTIVE COUNTER & ONLINE ORDERS (Takeaway, Zomato, Swiggy, Delivery) */}
            {(() => {
                const activeBranchId = (typeof getActiveBranchId === 'function' ? getActiveBranchId() : null) || currentUser?.branch_id || localStorage.getItem('dinexa_active_branch_id') || 1;
                const allActiveParcelOrders = (orders || []).filter(o => {
                    if (o.branch_id && String(o.branch_id) !== String(activeBranchId)) return false;
                    const st = String(o.status || '').toLowerCase();
                    if (st === 'cancelled' || st === 'served' || st === 'paid' || st === 'settled') return false;
                    // Strict Non-Table check: Must be Counter / Parcel / Online order, NEVER a physical dining table
                    const rawT = String(o.table_id || o.tableId || '').toLowerCase().trim();
                    const isCounterOrOnline = (rawT === 'counter' || !rawT || rawT === 'null' || rawT === 'undefined' || rawT.startsWith('ord-'));
                    return isCounterOrOnline;
                });

                const countZomato = allActiveParcelOrders.filter(o => {
                    const src = String(o.order_source || o.orderSource || '').toLowerCase();
                    const tp = String(o.type || '').toLowerCase();
                    return src === 'zomato' || tp.includes('zomato');
                }).length;
                const countSwiggy = allActiveParcelOrders.filter(o => {
                    const src = String(o.order_source || o.orderSource || '').toLowerCase();
                    const tp = String(o.type || '').toLowerCase();
                    return src === 'swiggy' || tp.includes('swiggy');
                }).length;
                const countDelivery = allActiveParcelOrders.filter(o => {
                    const src = String(o.order_source || o.orderSource || '').toLowerCase();
                    const tp = String(o.type || '').toLowerCase();
                    return src === 'delivery' || tp.includes('delivery');
                }).length;
                const countTakeaway = allActiveParcelOrders.filter(o => {
                    const src = String(o.order_source || o.orderSource || '').toLowerCase();
                    const tp = String(o.type || '').toLowerCase();
                    return src === 'takeaway' || o.table_id === 'counter' || o.tableId === 'counter' || tp.includes('takeaway') || tp.includes('counter') || tp.includes('parcel');
                }).length;

                const displayedCounterOrders = allActiveParcelOrders.filter(o => {
                    if (counterChannelFilter === 'all') return true;
                    const src = String(o.order_source || o.orderSource || '').toLowerCase();
                    const tp = String(o.type || '').toLowerCase();
                    const isZ = src === 'zomato' || tp.includes('zomato');
                    const isS = src === 'swiggy' || tp.includes('swiggy');
                    const isD = src === 'delivery' || tp.includes('delivery');
                    const isT = src === 'takeaway' || o.table_id === 'counter' || o.tableId === 'counter' || tp.includes('takeaway') || tp.includes('counter') || tp.includes('parcel');

                    if (counterChannelFilter === 'zomato') return isZ;
                    if (counterChannelFilter === 'swiggy') return isS;
                    if (counterChannelFilter === 'delivery') return isD;
                    if (counterChannelFilter === 'takeaway') return isT;
                    return true;
                });

                return (
                    <Box sx={{ mb: 3, p: 2, bgcolor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 900, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <TakeoutDiningIcon sx={{ color: '#008751' }} />
                                    {isMr ? '📦 चालू पार्सल व ऑनलाइन ऑर्डर्स' : '📦 Active Parcel & Online Orders'}
                                </Typography>
                                <Chip 
                                    label={allActiveParcelOrders.length} 
                                    size="small" 
                                    sx={{ bgcolor: allActiveParcelOrders.length > 0 ? '#ef4444' : '#94a3b8', color: '#ffffff', fontWeight: 900, height: 22 }} 
                                />
                            </Box>

                            {/* Quick Channel Filter Chips */}
                            <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap' }}>
                                <Chip
                                    label={`${isMr ? 'सर्व' : 'All'} (${allActiveParcelOrders.length})`}
                                    size="small"
                                    onClick={() => setCounterChannelFilter('all')}
                                    sx={{
                                        fontWeight: 800, fontSize: '0.75rem', height: 26,
                                        bgcolor: counterChannelFilter === 'all' ? '#0f172a' : '#f1f5f9',
                                        color: counterChannelFilter === 'all' ? '#ffffff' : '#334155'
                                    }}
                                />
                                <Chip
                                    label={`📦 Parcel (${countTakeaway})`}
                                    size="small"
                                    onClick={() => setCounterChannelFilter('takeaway')}
                                    sx={{
                                        fontWeight: 800, fontSize: '0.75rem', height: 26,
                                        bgcolor: counterChannelFilter === 'takeaway' ? '#475569' : '#f8fafc',
                                        color: counterChannelFilter === 'takeaway' ? '#ffffff' : '#475569',
                                        border: '1px solid #cbd5e1'
                                    }}
                                />
                                <Chip
                                    label={`🔴 Zomato (${countZomato})`}
                                    size="small"
                                    onClick={() => setCounterChannelFilter('zomato')}
                                    sx={{
                                        fontWeight: 800, fontSize: '0.75rem', height: 26,
                                        bgcolor: counterChannelFilter === 'zomato' ? '#cb202d' : '#fef2f2',
                                        color: counterChannelFilter === 'zomato' ? '#ffffff' : '#cb202d',
                                        border: '1px solid #fecaca'
                                    }}
                                />
                                <Chip
                                    label={`🟠 Swiggy (${countSwiggy})`}
                                    size="small"
                                    onClick={() => setCounterChannelFilter('swiggy')}
                                    sx={{
                                        fontWeight: 800, fontSize: '0.75rem', height: 26,
                                        bgcolor: counterChannelFilter === 'swiggy' ? '#fc8019' : '#fff7ed',
                                        color: counterChannelFilter === 'swiggy' ? '#ffffff' : '#fc8019',
                                        border: '1px solid #fed7aa'
                                    }}
                                />
                                <Chip
                                    label={`🛵 Delivery (${countDelivery})`}
                                    size="small"
                                    onClick={() => setCounterChannelFilter('delivery')}
                                    sx={{
                                        fontWeight: 800, fontSize: '0.75rem', height: 26,
                                        bgcolor: counterChannelFilter === 'delivery' ? '#0284c7' : '#f0f9ff',
                                        color: counterChannelFilter === 'delivery' ? '#ffffff' : '#0284c7',
                                        border: '1px solid #bae6fd'
                                    }}
                                />

                                <Button
                                    size="small"
                                    variant="contained"
                                    onClick={() => {
                                        if (typeof startNewParcelOrder === 'function') {
                                            startNewParcelOrder(counterChannelFilter !== 'all' ? counterChannelFilter : 'takeaway');
                                        } else {
                                            selectTable('counter');
                                        }
                                        if (typeof setActiveView === 'function') setActiveView('pos');
                                    }}
                                    startIcon={<AddIcon />}
                                    sx={{
                                        ml: 1,
                                        bgcolor: '#008751',
                                        color: '#ffffff',
                                        fontWeight: 800,
                                        fontSize: '0.75rem',
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        height: 26,
                                        '&:hover': { bgcolor: '#006f42' }
                                    }}
                                >
                                    + {isMr ? 'नवीन पार्सल' : 'New Order'}
                                </Button>
                            </Box>
                        </Box>

                        {displayedCounterOrders.length > 0 ? (
                            <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 1, pt: 0.5, '&::-webkit-scrollbar': { height: 6 }, '&::-webkit-scrollbar-thumb': { bgcolor: '#cbd5e1', borderRadius: 3 } }}>
                                {displayedCounterOrders.map(order => {
                                    const isZomato = (order.order_source === 'zomato' || order.orderSource === 'zomato' || (order.type && order.type.toLowerCase().includes('zomato')));
                                    const isSwiggy = (order.order_source === 'swiggy' || order.orderSource === 'swiggy' || (order.type && order.type.toLowerCase().includes('swiggy')));
                                    const isDelivery = (order.order_source === 'delivery' || order.orderSource === 'delivery' || (order.type && order.type.toLowerCase().includes('delivery')));
                                    
                                    const channelBg = isZomato ? '#cb202d' : (isSwiggy ? '#fc8019' : (isDelivery ? '#0284c7' : '#475569'));
                                    
                                    // Match linked dining table if any
                                    const matchedTable = (tables || []).find(t => 
                                        String(t.activeOrderId) === String(order.id) || 
                                        (order.table_id && order.table_id !== 'counter' && order.table_id !== 'null' && (String(t.id) === String(order.table_id || order.tableId) || String(t.number) === String(order.table_id || order.tableId)))
                                    );
                                    const tableDisplayName = matchedTable ? matchedTable.number : (order.table_name || order.tableName || (order.table_id && order.table_id !== 'counter' && order.table_id !== 'null' ? `Table ${order.table_id}` : 'Counter'));
                                    
                                    const channelLabel = isZomato 
                                        ? `🔴 Zomato ${order.aggregator_order_id ? `#${order.aggregator_order_id}` : ''}`
                                        : (isSwiggy 
                                            ? `🟠 Swiggy ${order.aggregator_order_id ? `#${order.aggregator_order_id}` : ''}`
                                            : (isDelivery ? '🛵 Direct Delivery' : (tableDisplayName !== 'Counter' ? `📦 Parcel` : '📦 Counter Parcel')));

                                    const itemsCount = (order.items || []).reduce((sum, it) => sum + (Number(it.quantity) || 1), 0);
                                    const orderTotal = Number(order.total || order.subtotal || 0);
                                    const orderDateDisplay = formatDateTimeIST(order.created_at || order.timestamp || order.createdAt);

                                    return (
                                        <Card
                                            key={order.id}
                                            elevation={0}
                                            onClick={() => {
                                                selectTable(order.id);
                                                if (typeof setActiveView === 'function') setActiveView('pos');
                                            }}
                                            sx={{
                                                minWidth: 270,
                                                maxWidth: 310,
                                                borderRadius: 3,
                                                border: '1.5px solid #cbd5e1',
                                                bgcolor: '#f8fafc',
                                                p: 1.5,
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'space-between',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    borderColor: '#008751',
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: '0 4px 12px rgba(0,135,81,0.12)'
                                                }
                                            }}
                                        >
                                            <Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 0.6 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flexWrap: 'wrap' }}>
                                                        <Chip 
                                                            label={channelLabel} 
                                                            size="small" 
                                                            sx={{ bgcolor: channelBg, color: '#ffffff', fontWeight: 900, fontSize: '0.72rem', height: 22 }} 
                                                        />
                                                        {tableDisplayName && (
                                                            <Chip 
                                                                label={tableDisplayName === 'Counter' ? '🏢 Counter' : `🪑 ${tableDisplayName}`} 
                                                                size="small" 
                                                                sx={{ 
                                                                    bgcolor: tableDisplayName === 'Counter' ? '#f1f5f9' : '#e0e7ff', 
                                                                    color: tableDisplayName === 'Counter' ? '#475569' : '#3730a3', 
                                                                    fontWeight: 900, 
                                                                    fontSize: '0.75rem', 
                                                                    height: 22,
                                                                    border: tableDisplayName === 'Counter' ? '1px solid #cbd5e1' : '1.5px solid #818cf8'
                                                                }} 
                                                            />
                                                        )}
                                                    </Box>
                                                    <Chip 
                                                        label={order.status === 'billed' ? '🟡 BILLED' : '🔥 COOKING'} 
                                                        size="small" 
                                                        sx={{ bgcolor: order.status === 'billed' ? '#fef3c7' : '#fef2f2', color: order.status === 'billed' ? '#b45309' : '#dc2626', fontWeight: 900, fontSize: '0.68rem', height: 20 }} 
                                                    />
                                                </Box>

                                                {/* Local Date & Time and Order ID */}
                                                <Typography variant="body2" sx={{ fontWeight: 800, color: '#0f172a', mb: 0.4, fontSize: '0.85rem' }}>
                                                    📅 {orderDateDisplay} ({order.id})
                                                </Typography>

                                                <Typography variant="caption" sx={{ color: '#64748b', display: 'block', fontWeight: 600 }}>
                                                    🍽️ {itemsCount} {isMr ? 'पदार्थ' : 'Items'} • 👨‍🍳 {order.waiter_name || order.waiterName || 'Staff'}
                                                </Typography>
                                            </Box>

                                            <Box sx={{ mt: 1.5, pt: 1, borderTop: '1px dashed #cbd5e1', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 900, color: '#008751' }}>
                                                    ₹{orderTotal.toFixed(0)}
                                                </Typography>

                                                <Box sx={{ display: 'flex', gap: 0.8 }} onClick={(e) => e.stopPropagation()}>
                                                    {/* Cancel Button ONLY visible to Owner / Admin */}
                                                    {isOwnerOrAdmin && (
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            color="error"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (window.confirm(isMr ? 'ही ऑर्डर रद्द करायची का?' : 'Cancel this order?')) {
                                                                    if (typeof cancelOrder === 'function') {
                                                                        cancelOrder('Cancelled by owner', matchedTable ? matchedTable.id : (order.table_id || 'counter'), order.id);
                                                                    }
                                                                }
                                                            }}
                                                            sx={{ minWidth: 32, px: 0.8, py: 0.3, fontSize: '0.7rem', fontWeight: 800, borderRadius: 1.5 }}
                                                        >
                                                            ✕
                                                        </Button>
                                                    )}

                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            selectTable(order.id);
                                                            if (typeof setActiveView === 'function') setActiveView('pos');
                                                        }}
                                                        sx={{
                                                            color: '#0284c7',
                                                            borderColor: '#bae6fd',
                                                            fontWeight: 800,
                                                            fontSize: '0.72rem',
                                                            py: 0.4,
                                                            px: 1,
                                                            borderRadius: 1.5,
                                                            textTransform: 'none',
                                                            '&:hover': { bgcolor: '#f0f9ff', borderColor: '#0284c7' }
                                                        }}
                                                    >
                                                        ✏️ {isMr ? 'बदला' : 'Edit'}
                                                    </Button>

                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleOpenCheckout(matchedTable ? { id: matchedTable.id, number: matchedTable.number, activeOrderId: order.id } : (order.table_id && order.table_id !== 'counter' ? { id: order.table_id, number: order.table_id, activeOrderId: order.id } : { id: order.id, tableId: order.table_id || 'counter', number: tableDisplayName, activeOrderId: order.id }));
                                                        }}
                                                        startIcon={<PointOfSaleIcon sx={{ fontSize: 14 }} />}
                                                        sx={{
                                                            bgcolor: '#059669',
                                                            color: '#ffffff',
                                                            fontWeight: 900,
                                                            fontSize: '0.72rem',
                                                            py: 0.4,
                                                            px: 1.2,
                                                            borderRadius: 1.5,
                                                            textTransform: 'none',
                                                            '&:hover': { bgcolor: '#047857' }
                                                        }}
                                                    >
                                                        ⚡ {isMr ? 'बिल सेटल' : 'Settle'}
                                                    </Button>
                                                </Box>
                                            </Box>
                                        </Card>
                                    );
                                })}
                            </Box>
                        ) : (
                            <Typography variant="caption" sx={{ color: '#94a3b8', fontStyle: 'italic', display: 'block', textAlign: 'center', py: 1 }}>
                                {isMr ? 'कोणतीही चालू पार्सल / ऑनलाइन ऑर्डर नाही.' : 'No active counter or online orders.'}
                            </Typography>
                        )}
                    </Box>
                );
            })()}

            {/* Dynamic Section Tabs */}
            {(() => {
                const sortedSections = sections && Array.isArray(sections) && sections.length > 0
                    ? [...sections].sort((a, b) => (parseInt(a.display_order) || 0) - (parseInt(b.display_order) || 0))
                    : [];

                if (sortedSections.length === 0) return null;

                return (
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3, bgcolor: '#ffffff', borderRadius: 2, px: 2 }}>
                        <Tabs value={activeSectionTab} onChange={(e, val) => setActiveSectionTab(val)} variant="scrollable" scrollButtons="auto">
                            {sortedSections.map(s => (
                                <Tab key={s.id} label={s.name} value={s.id} sx={{ fontWeight: 800, fontSize: '0.95rem' }} />
                            ))}
                        </Tabs>
                    </Box>
                );
            })()}

            {/* Tables Cards Grid */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5, justifyContent: 'flex-start' }}>
                {displayedTables.map(table => {
                    const color = getStatusColor(table.status);
                    const bg = getStatusBg(table.status);
                    const cleanTNum = String(table?.number || table?.id || '').replace(/[^0-9]/g, '');
                    const activeOrder = table.activeOrderId 
                        ? (orders || []).find(o => String(o.id) === String(table.activeOrderId) || String(o.order_id) === String(table.activeOrderId)) 
                        : (orders || []).find(o => {
                            if (['served', 'cancelled'].includes(String(o.status).toLowerCase())) return false;
                            const oTable = String(o.table_id || o.tableId || '').trim();
                            const oClean = oTable.replace(/[^0-9]/g, '');
                            return oTable === String(table.id) || oTable === String(table.number) || (cleanTNum && oClean === cleanTNum);
                        });
                    const itemsCount = activeOrder ? (activeOrder.items || []).reduce((sum, i) => sum + (Number(i.quantity) || 1), 0) : 0;
                    const isOccupiedOrBilled = table.status === 'occupied' || table.status === 'billed';

                    let showMerge = false;
                    if (table.isSubTable && table.id.endsWith('-a')) {
                        const siblings = tables.filter(t => t.parentId === table.parentId);
                        showMerge = siblings.every(t => t.status === 'vacant' || !t.status);
                    }

                    return (
                        <Box key={table.id} sx={{ width: 175, flexShrink: 0, flexGrow: 0 }}>
                            <Card 
                                onClick={() => handleCardClick(table)}
                                sx={{
                                    width: 175,
                                    minHeight: 185,
                                    height: '100%',
                                    cursor: 'pointer',
                                    position: 'relative',
                                    borderRadius: 3.5,
                                    border: table.isSubTable ? `2px dashed ${color}` : `2px solid ${color}`,
                                    bgcolor: bg,
                                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                                    '&:hover': {
                                        transform: 'translateY(-3px)',
                                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                                    },
                                    transition: 'all 0.15s ease-in-out'
                                }}
                            >
                                <IconButton
                                    size="small"
                                    onClick={(e) => { e.stopPropagation(); togglePinTable(table.id); }}
                                    sx={{
                                        position: 'absolute', top: 6, right: 6,
                                        bgcolor: (Array.isArray(pinnedTables) && pinnedTables.includes(table.id)) ? '#ea580c' : 'rgba(255,255,255,0.85)',
                                        color: (Array.isArray(pinnedTables) && pinnedTables.includes(table.id)) ? '#ffffff' : '#64748b',
                                        p: '2px',
                                        zIndex: 2,
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                        '&:hover': { bgcolor: (Array.isArray(pinnedTables) && pinnedTables.includes(table.id)) ? '#c2410c' : '#ffffff' }
                                    }}
                                >
                                    {(Array.isArray(pinnedTables) && pinnedTables.includes(table.id)) ? <PushPinIcon sx={{ fontSize: 13 }} /> : <PushPinOutlinedIcon sx={{ fontSize: 13 }} />}
                                </IconButton>
                                
                                <CardContent sx={{ p: 2, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.8 }}>
                                    <TableBarIcon sx={{ fontSize: 40, color }} />
                                    
                                    <Typography variant="h6" sx={{ fontWeight: 900, color: '#111827' }}>
                                        {table.number}
                                    </Typography>
                                    
                                    <Chip 
                                        label={getStatusLabel(table.status)} 
                                        size="small" 
                                        sx={{ 
                                            bgcolor: color + '20', 
                                            color, 
                                            fontWeight: 900, 
                                            fontSize: '0.65rem',
                                            height: 20,
                                            px: 0.5
                                        }} 
                                    />

                                    {isOccupiedOrBilled && activeOrder && itemsCount > 0 ? (
                                        <Typography variant="caption" sx={{ color: '#1e293b', fontWeight: 800, mt: 0.2, bgcolor: '#ffffff', px: 1, py: 0.3, borderRadius: 1.5, border: '1px solid #cbd5e1' }}>
                                            {itemsCount} नग | ₹{Number(activeOrder.total || 0).toFixed(0)}
                                        </Typography>
                                    ) : (
                                        <Typography variant="caption" sx={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '0.7rem', mt: 0.2 }}>
                                            {table.status === 'dirty' || table.status === 'cleaning' 
                                                ? (isMr ? 'सफाई बाकी' : 'Cleaning') 
                                                : (isMr ? 'ऑर्डर नाही' : 'No order')}
                                        </Typography>
                                    )}

                                    {/* 1-Tap Quick Checkout Button directly on card */}
                                    {isOccupiedOrBilled && activeOrder && itemsCount > 0 && (
                                        <Button
                                            size="small"
                                            variant="contained"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleOpenCheckout(table);
                                            }}
                                            startIcon={<PointOfSaleIcon sx={{ fontSize: 13 }} />}
                                            sx={{
                                                mt: 0.5,
                                                bgcolor: '#059669',
                                                color: '#ffffff',
                                                fontWeight: 900,
                                                fontSize: '0.65rem',
                                                py: 0.3,
                                                px: 1,
                                                borderRadius: 2,
                                                textTransform: 'none',
                                                '&:hover': { bgcolor: '#047857' }
                                            }}
                                        >
                                            ⚡ {isMr ? 'बिल सेटल' : 'Checkout'}
                                        </Button>
                                    )}

                                    {showMerge && (
                                        <Button
                                            size="small"
                                            variant="contained"
                                            color="primary"
                                            startIcon={<CallMergeIcon sx={{ fontSize: 12 }} />}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                mergeSubTables(table.parentId);
                                            }}
                                            sx={{ 
                                                mt: 0.5, 
                                                textTransform: 'none', 
                                                fontWeight: 800, 
                                                fontSize: '0.65rem', 
                                                py: 0.3,
                                                bgcolor: '#008751',
                                                '&:hover': { bgcolor: '#006f42' }
                                            }}
                                        >
                                            Merge
                                        </Button>
                                    )}
                                </CardContent>
                            </Card>
                        </Box>
                    );
                })}
            </Box>

            {/* Table Action Options Dialog (Improved UX as per Image 3) */}
            <Dialog 
                open={actionDialogOpen} 
                onClose={() => setActionDialogOpen(false)}
                PaperProps={{ sx: { borderRadius: 4, width: 420 } }}
            >
                <DialogTitle sx={{ fontWeight: 900, bgcolor: getStatusColor(selectedTable?.status), color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TableBarIcon /> {selectedTable?.number} - {getStatusLabel(selectedTable?.status)}
                    </Box>
                    <IconButton size="small" onClick={() => setActionDialogOpen(false)} sx={{ color: '#ffffff' }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                
                <DialogContent sx={{ p: 3 }}>
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        
                        {/* STAR OPTION: Catchy Instant Checkout Button */}
                        {(selectedTable?.status === 'occupied' || selectedTable?.status === 'billed') && (
                            <Button
                                fullWidth
                                variant="contained"
                                size="large"
                                startIcon={<PointOfSaleIcon sx={{ fontSize: 24 }} />}
                                onClick={() => handleOpenCheckout(selectedTable)}
                                sx={{
                                    py: 1.5,
                                    fontWeight: 900,
                                    bgcolor: '#059669',
                                    color: '#ffffff',
                                    '&:hover': { bgcolor: '#047857' },
                                    borderRadius: 3,
                                    textTransform: 'none',
                                    fontSize: '1.05rem',
                                    boxShadow: '0 4px 14px rgba(5, 150, 105, 0.4)'
                                }}
                            >
                                ⚡ {isMr ? 'थेट बिल सेटल करा (Instant Checkout)' : 'Instant Checkout & Settle Bill'}
                            </Button>
                        )}

                        {/* Special Action for Dirty Table */}
                        {(selectedTable?.status === 'dirty' || selectedTable?.status === 'cleaning') && (
                            <Button
                                variant="contained"
                                size="large"
                                startIcon={<CleaningServicesIcon />}
                                onClick={() => handleUpdateStatus('vacant')}
                                sx={{
                                    py: 1.4,
                                    fontWeight: 900,
                                    bgcolor: '#8b5cf6',
                                    '&:hover': { bgcolor: '#7c3aed' },
                                    borderRadius: 3,
                                    textTransform: 'none',
                                    fontSize: '1rem'
                                }}
                            >
                                {isMr ? '🧹 टेबल साफ झाला (Mark Clean & Vacant)' : '🧹 Mark Clean & Vacant'}
                            </Button>
                        )}

                        {/* Option 2: Open in POS Terminal */}
                        <Button
                            variant={(selectedTable?.status === 'occupied' || selectedTable?.status === 'billed') ? "outlined" : "contained"}
                            size="large"
                            startIcon={<ReceiptLongIcon />}
                            onClick={handleStartOrder}
                            sx={{
                                py: 1.3,
                                fontWeight: 900,
                                bgcolor: (selectedTable?.status === 'occupied' || selectedTable?.status === 'billed') ? 'transparent' : '#ea580c',
                                color: (selectedTable?.status === 'occupied' || selectedTable?.status === 'billed') ? '#ea580c' : '#ffffff',
                                borderColor: '#ea580c',
                                '&:hover': { bgcolor: (selectedTable?.status === 'occupied' || selectedTable?.status === 'billed') ? '#fff7ed' : '#c2410c', borderColor: '#c2410c' },
                                borderRadius: 3,
                                textTransform: 'none',
                                fontSize: '0.95rem'
                            }}
                        >
                            {(selectedTable?.status === 'occupied' || selectedTable?.status === 'billed')
                                ? (isMr ? '🛒 ऑर्डर उघडा / नवीन KOT जोडा (POS)' : '🛒 Open Active Order in POS')
                                : (isMr ? '🛒 नवीन ऑर्डर सुरू करा (POS)' : '🛒 Start New Order')}
                        </Button>

                        <Divider sx={{ my: 0.5 }} />

                        {/* Option 3: Shift Table */}
                        {(selectedTable?.status === 'occupied' || selectedTable?.status === 'billed') && (
                            <Box sx={{ bgcolor: '#fff7ed', p: 2, borderRadius: 3, border: '1px solid #fed7aa' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#c2410c', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <SwapHorizIcon /> {isMr ? 'टेबल शिफ्ट करा (दुसऱ्या टेबलवर पाठवा)' : 'Shift Table Order'}
                                </Typography>
                                <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                                    <InputLabel>{isMr ? 'नवीन मोकळा टेबल निवडा' : 'Select Target Table'}</InputLabel>
                                    <Select
                                        value={targetShiftTableId}
                                        label={isMr ? 'नवीन मोकळा टेबल निवडा' : 'Select Target Table'}
                                        onChange={(e) => setTargetShiftTableId(e.target.value)}
                                    >
                                        {diningTables
                                            .filter(t => t.id !== selectedTable?.id && (t.status === 'vacant' || !t.status))
                                            .map(t => (
                                                <MenuItem key={t.id} value={t.id}>{t.number}</MenuItem>
                                            ))
                                        }
                                    </Select>
                                </FormControl>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    disabled={!targetShiftTableId}
                                    onClick={handleConfirmShift}
                                    sx={{ fontWeight: 800, bgcolor: '#ea580c', textTransform: 'none', borderRadius: 2 }}
                                >
                                    {isMr ? 'शिफ्ट कन्फर्म करा' : 'Confirm Shift'}
                                </Button>
                            </Box>
                        )}

                        {/* Option 4: Status Quick Buttons */}
                        <Box sx={{ display: 'flex', gap: 1.2, flexWrap: 'wrap' }}>
                            {selectedTable?.status === 'reserved' ? (
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    color="success"
                                    startIcon={<CleaningServicesIcon />}
                                    onClick={() => handleUpdateStatus('vacant')}
                                    sx={{ fontWeight: 800, py: 1, textTransform: 'none', borderRadius: 2 }}
                                >
                                    {isMr ? '🟢 मोकळा करा' : '🟢 Mark Vacant'}
                                </Button>
                            ) : (
                                (selectedTable?.status === 'vacant' || !selectedTable?.status) && (
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        color="primary"
                                        startIcon={<EventSeatIcon />}
                                        onClick={() => handleUpdateStatus('reserved')}
                                        sx={{ fontWeight: 800, py: 1, textTransform: 'none', borderRadius: 2 }}
                                    >
                                        {isMr ? '🔵 टेबल आरक्षित करा' : '🔵 Reserve Table'}
                                    </Button>
                                )
                            )}

                            {(selectedTable?.status === 'occupied' || selectedTable?.status === 'billed') && (
                                <Box sx={{ display: 'flex', gap: 1, width: '100%', flexWrap: 'wrap' }}>
                                    <Button
                                        sx={{ flex: 1, minWidth: 100, fontWeight: 800, py: 1, textTransform: 'none', borderRadius: 2, borderColor: '#8b5cf6', color: '#8b5cf6' }}
                                        variant="outlined"
                                        startIcon={<CleaningServicesIcon />}
                                        onClick={() => handleUpdateStatus('dirty')}
                                    >
                                        {isMr ? '🧹 सफाई बाकी' : 'Mark Dirty'}
                                    </Button>

                                    <Button
                                        sx={{ flex: 1, minWidth: 100, fontWeight: 800, py: 1, textTransform: 'none', borderRadius: 2, borderColor: '#f59e0b', color: '#b45309', '&:hover': { bgcolor: '#fef3c7' } }}
                                        variant="outlined"
                                        startIcon={<DeleteSweepIcon />}
                                        onClick={() => handleUpdateStatus('vacant')}
                                    >
                                        {isMr ? '🗑️ मोकळा करा' : 'Free Table'}
                                    </Button>

                                    <Button
                                        sx={{ flex: 1, minWidth: 100, fontWeight: 800, py: 1, textTransform: 'none', borderRadius: 2 }}
                                        variant="outlined"
                                        color="error"
                                        startIcon={<CancelIcon />}
                                        onClick={() => {
                                            setActionDialogOpen(false);
                                            setCancelModalOpen(true);
                                        }}
                                    >
                                        {isMr ? '❌ रद्द करा' : 'Cancel'}
                                    </Button>
                                </Box>
                            )}
                        </Box>

                        {/* Option 5: Split Table */}
                        {(selectedTable?.status === 'vacant' || !selectedTable?.status) && !selectedTable?.isSubTable && (
                            <Box sx={{ borderTop: '1px solid #e2e8f0', pt: 1 }}>
                                {!isSplitMode ? (
                                    <Button
                                        fullWidth
                                        variant="text"
                                        onClick={() => setIsSplitMode(true)}
                                        sx={{ color: '#64748b', fontWeight: 800, textTransform: 'none', fontSize: '0.85rem' }}
                                    >
                                        ✂️ {isMr ? 'टेबल विभागून द्या (Split Table)' : 'Split Table for Multiple Customers'}
                                    </Button>
                                ) : (
                                    <Box sx={{ bgcolor: '#f1f5f9', p: 2, borderRadius: 2.5 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 800, color: '#334155', display: 'block', mb: 1 }}>
                                            {isMr ? 'किती सब-टेबल्स तयार करायचे?' : 'Select number of sub-tables:'}
                                        </Typography>
                                        <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                                            <Select
                                                value={splitCount}
                                                onChange={(e) => setSplitCount(e.target.value)}
                                            >
                                                <MenuItem value={2}>2 Sub-tables (A & B)</MenuItem>
                                                <MenuItem value={3}>3 Sub-tables (A, B & C)</MenuItem>
                                                <MenuItem value={4}>4 Sub-tables (A, B, C & D)</MenuItem>
                                            </Select>
                                        </FormControl>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            onClick={handleConfirmSplit}
                                            sx={{ fontWeight: 800, bgcolor: '#475569', textTransform: 'none', borderRadius: 2 }}
                                        >
                                            {isMr ? 'स्प्लिट कन्फर्म करा' : 'Confirm Split'}
                                        </Button>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                
                <DialogActions sx={{ p: 2, bgcolor: '#f8fafc' }}>
                    <Button onClick={() => setActionDialogOpen(false)} sx={{ fontWeight: 700, color: '#64748b' }}>
                        {isMr ? 'बंद करा (Close)' : 'Close'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* LIVE BILL BREAKDOWN CHECKOUT MODAL (Transparent & Clear UI/UX) */}
            <Dialog
                open={checkoutModalOpen}
                onClose={() => setCheckoutModalOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 4, p: 0.5 } }}
            >
                <DialogTitle sx={{ fontWeight: 900, bgcolor: '#059669', color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ReceiptLongIcon /> {isMr ? `टेबल ${checkoutTable?.number} - बिल सेटल करा (Checkout)` : `Table ${checkoutTable?.number} - Checkout & Settle`}
                    </Box>
                    <IconButton size="small" onClick={() => setCheckoutModalOpen(false)} sx={{ color: '#ffffff' }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f8fafc' }}>
                    {currentCheckoutOrder ? (
                        <Box>
                            {/* Hidden container for printing */}
                            <Box 
                                id="live-checkout-slip" 
                                sx={{ 
                                    bgcolor: '#ffffff', 
                                    p: 2, 
                                    border: '1px solid #e2e8f0', 
                                    borderRadius: 3, 
                                    mb: 2.5,
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)' 
                                }}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, pb: 1, borderBottom: '1px solid #e2e8f0' }}>
                                    <Box>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 900, color: '#0f172a' }}>
                                            {checkoutTable?.id === 'counter' ? '📦 Counter / Parcel' : `🍽️ Table ${checkoutTable?.number}`}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>
                                            📅 {formatDateTimeIST(currentCheckoutOrder.created_at || currentCheckoutOrder.timestamp || currentCheckoutOrder.createdAt)}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ textAlign: 'right' }}>
                                        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, display: 'block' }}>
                                            👨‍🍳 {currentCheckoutOrder.waiter_name || currentCheckoutOrder.waiterName || 'Staff'}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                                            {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Itemized List */}
                                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #f1f5f9', mb: 1.5 }}>
                                    <Table size="small">
                                        <TableHead sx={{ bgcolor: '#f8fafc' }}>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 900, color: '#475569', fontSize: '0.8rem' }}>
                                                    {isMr ? 'पदार्थ' : 'Item'}
                                                </TableCell>
                                                <TableCell align="center" sx={{ fontWeight: 900, color: '#475569', fontSize: '0.8rem' }}>
                                                    {isMr ? 'नग' : 'Qty'}
                                                </TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 900, color: '#475569', fontSize: '0.8rem' }}>
                                                    {isMr ? 'दर' : 'Rate'}
                                                </TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 900, color: '#475569', fontSize: '0.8rem' }}>
                                                    {isMr ? 'रक्कम' : 'Total'}
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {(currentCheckoutOrder.items || []).map((item, idx) => {
                                                const itemName = isMr ? (item.name_mr || item.name) : (isHi ? (item.name_hi || item.name) : item.name);
                                                const itemTotal = (Number(item.price) || 0) * (Number(item.quantity) || 1);
                                                return (
                                                    <TableRow key={idx}>
                                                        <TableCell sx={{ fontWeight: 800, fontSize: '0.85rem' }}>
                                                            {itemName}
                                                            {item.remarks && (
                                                                <Typography variant="caption" sx={{ display: 'block', color: '#dc2626', fontStyle: 'italic', fontSize: '0.7rem' }}>
                                                                    * {item.remarks}
                                                                </Typography>
                                                            )}
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ fontWeight: 900, fontSize: '0.85rem' }}>
                                                            x{item.quantity}
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ fontSize: '0.85rem' }}>
                                                            ₹{Number(item.price).toFixed(0)}
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 900, fontSize: '0.85rem' }}>
                                                            ₹{itemTotal.toFixed(0)}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                {/* Bill Calculations */}
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8, px: 1 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', color: '#475569' }}>
                                        <span>Subtotal:</span>
                                        <span style={{ fontWeight: 800 }}>₹{checkoutSubtotal.toFixed(2)}</span>
                                    </Box>

                                    {enableGst && (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#475569' }}>
                                            <span>GST (5%):</span>
                                            <span style={{ fontWeight: 800 }}>₹{checkoutTax.toFixed(2)}</span>
                                        </Box>
                                    )}

                                    {checkoutDiscountAmount > 0 && (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#dc2626' }}>
                                            <span>Discount:</span>
                                            <span style={{ fontWeight: 800 }}>- ₹{checkoutDiscountAmount.toFixed(2)}</span>
                                        </Box>
                                    )}

                                    <Divider sx={{ my: 0.5 }} />

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="h6" sx={{ fontWeight: 900, color: '#0f172a' }}>
                                            {isMr ? 'एकूण बिल (Total Amount):' : 'Grand Total:'}
                                        </Typography>
                                        <Typography variant="h5" sx={{ fontWeight: 900, color: '#059669' }}>
                                            ₹{checkoutGrandTotal.toFixed(0)}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>

                            {/* Discount & Payment Method Controls */}
                            <Box sx={{ bgcolor: '#ffffff', p: 2, border: '1px solid #e2e8f0', borderRadius: 3, mb: 1 }}>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            label={isMr ? "सूट रक्कम (Discount ₹)" : "Discount Amount (₹)"}
                                            type="number"
                                            size="small"
                                            fullWidth
                                            value={checkoutDiscount || ''}
                                            onChange={(e) => {
                                                const dVal = parseFloat(e.target.value) || 0;
                                                setCheckoutDiscount(dVal);
                                                if (checkoutPaymentMethod === 'split') {
                                                    const newTotal = Math.max(0, checkoutSubtotal + checkoutTax - dVal);
                                                    setCheckoutSplitCash(newTotal);
                                                    setCheckoutSplitUpi(0);
                                                }
                                            }}
                                            InputProps={{ inputProps: { min: 0 } }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={8}>
                                        {(() => {
                                            const isZomato = currentCheckoutOrder && (currentCheckoutOrder.order_source === 'zomato' || currentCheckoutOrder.orderSource === 'zomato' || (currentCheckoutOrder.type && currentCheckoutOrder.type.toLowerCase().includes('zomato')));
                                            const isSwiggy = currentCheckoutOrder && (currentCheckoutOrder.order_source === 'swiggy' || currentCheckoutOrder.orderSource === 'swiggy' || (currentCheckoutOrder.type && currentCheckoutOrder.type.toLowerCase().includes('swiggy')));

                                            if (isZomato) {
                                                return (
                                                    <Button
                                                        fullWidth
                                                        variant="contained"
                                                        disabled
                                                        sx={{ 
                                                            fontWeight: 900, 
                                                            bgcolor: '#cb202d !important', 
                                                            color: '#ffffff !important',
                                                            textTransform: 'none',
                                                            borderRadius: 2,
                                                            py: 1,
                                                            fontSize: '0.92rem'
                                                        }}
                                                    >
                                                        🔴 Zomato Payout
                                                    </Button>
                                                );
                                            }

                                            if (isSwiggy) {
                                                return (
                                                    <Button
                                                        fullWidth
                                                        variant="contained"
                                                        disabled
                                                        sx={{ 
                                                            fontWeight: 900, 
                                                            bgcolor: '#fc8019 !important', 
                                                            color: '#ffffff !important',
                                                            textTransform: 'none',
                                                            borderRadius: 2,
                                                            py: 1,
                                                            fontSize: '0.92rem'
                                                        }}
                                                    >
                                                        🟠 Swiggy Payout
                                                    </Button>
                                                );
                                            }

                                            return (
                                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1 }}>
                                                    <Button
                                                        variant={checkoutPaymentMethod === 'cash' ? "contained" : "outlined"}
                                                        onClick={() => setCheckoutPaymentMethod('cash')}
                                                        startIcon={<PaymentsIcon sx={{ fontSize: 16 }} />}
                                                        sx={{ 
                                                            fontWeight: 800, 
                                                            fontSize: '0.8rem',
                                                            bgcolor: checkoutPaymentMethod === 'cash' ? '#0f172a' : 'transparent',
                                                            color: checkoutPaymentMethod === 'cash' ? '#ffffff' : '#0f172a',
                                                            borderColor: '#0f172a',
                                                            textTransform: 'none',
                                                            borderRadius: 2,
                                                            py: 0.8,
                                                            px: 0.5
                                                        }}
                                                    >
                                                        💵 Cash
                                                    </Button>
                                                    <Button
                                                        variant={checkoutPaymentMethod === 'upi' ? "contained" : "outlined"}
                                                        onClick={() => setCheckoutPaymentMethod('upi')}
                                                        startIcon={<QrCode2Icon sx={{ fontSize: 16 }} />}
                                                        sx={{ 
                                                            fontWeight: 800, 
                                                            fontSize: '0.8rem',
                                                            bgcolor: checkoutPaymentMethod === 'upi' ? '#2563eb' : 'transparent',
                                                            color: checkoutPaymentMethod === 'upi' ? '#ffffff' : '#2563eb',
                                                            borderColor: '#2563eb',
                                                            textTransform: 'none',
                                                            borderRadius: 2,
                                                            py: 0.8,
                                                            px: 0.5
                                                        }}
                                                    >
                                                        📱 UPI
                                                    </Button>
                                                    <Button
                                                        variant={checkoutPaymentMethod === 'split' ? "contained" : "outlined"}
                                                        onClick={() => {
                                                            setCheckoutPaymentMethod('split');
                                                            setCheckoutSplitCash(checkoutGrandTotal);
                                                            setCheckoutSplitUpi(0);
                                                        }}
                                                        startIcon={<CallMergeIcon sx={{ transform: 'rotate(180deg)', fontSize: 16 }} />}
                                                        sx={{ 
                                                            fontWeight: 800, 
                                                            fontSize: '0.8rem',
                                                            bgcolor: checkoutPaymentMethod === 'split' ? '#ea580c' : 'transparent',
                                                            color: checkoutPaymentMethod === 'split' ? '#ffffff' : '#ea580c',
                                                            borderColor: '#ea580c',
                                                            textTransform: 'none',
                                                            borderRadius: 2,
                                                            py: 0.8,
                                                            px: 0.5
                                                        }}
                                                    >
                                                        ⚡ Split
                                                    </Button>
                                                    <Button
                                                        variant={checkoutPaymentMethod === 'free' ? "contained" : "outlined"}
                                                        onClick={() => {
                                                            setCheckoutPaymentMethod('free');
                                                            setCheckoutDiscount(checkoutSubtotal + checkoutTax);
                                                        }}
                                                        sx={{ 
                                                            fontWeight: 800, 
                                                            fontSize: '0.78rem',
                                                            bgcolor: checkoutPaymentMethod === 'free' ? '#16a34a' : 'transparent',
                                                            color: checkoutPaymentMethod === 'free' ? '#ffffff' : '#16a34a',
                                                            borderColor: '#16a34a',
                                                            textTransform: 'none',
                                                            borderRadius: 2,
                                                            py: 0.8,
                                                            px: 0.5
                                                        }}
                                                    >
                                                        🎁 Free
                                                    </Button>
                                                </Box>
                                            );
                                        })()}
                                    </Grid>

                                    {/* Split Payment Cash & UPI Breakdown Inputs */}
                                    {checkoutPaymentMethod === 'split' && (
                                        <Grid item xs={12}>
                                            <Box sx={{ p: 1.5, bgcolor: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 2.5 }}>
                                                <Typography variant="caption" sx={{ fontWeight: 800, color: '#c2410c', display: 'block', mb: 1 }}>
                                                    ⚡ {isMr ? 'स्प्लिट पेमेंट विभागणी (Cash + UPI):' : 'Split Payment Breakdown:'}
                                                </Typography>
                                                <Grid container spacing={1.5}>
                                                    <Grid item xs={6}>
                                                        <TextField
                                                            label={isMr ? "💵 रोख (Cash ₹)" : "💵 Cash (₹)"}
                                                            type="number"
                                                            size="small"
                                                            fullWidth
                                                            value={checkoutSplitCash}
                                                            onChange={(e) => {
                                                                const val = Math.max(0, parseFloat(e.target.value) || 0);
                                                                setCheckoutSplitCash(val);
                                                                setCheckoutSplitUpi(Math.max(0, checkoutGrandTotal - val));
                                                            }}
                                                            InputProps={{ inputProps: { min: 0, max: checkoutGrandTotal } }}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <TextField
                                                            label={isMr ? "📱 ऑनलाईन (UPI ₹)" : "📱 UPI (₹)"}
                                                            type="number"
                                                            size="small"
                                                            fullWidth
                                                            value={checkoutSplitUpi}
                                                            onChange={(e) => {
                                                                const val = Math.max(0, parseFloat(e.target.value) || 0);
                                                                setCheckoutSplitUpi(val);
                                                                setCheckoutSplitCash(Math.max(0, checkoutGrandTotal - val));
                                                            }}
                                                            InputProps={{ inputProps: { min: 0, max: checkoutGrandTotal } }}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                        </Grid>
                                    )}
                                </Grid>
                            </Box>
                        </Box>
                    ) : (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography variant="body1" sx={{ color: '#64748b', fontWeight: 800 }}>
                                ⚠️ या टेबलवर कोणतीही चालू ऑर्डर सापडली नाही.
                            </Typography>
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{ p: 2.5, bgcolor: '#ffffff', display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    <Button
                        variant="contained"
                        size="large"
                        onClick={() => handleConfirmSettle(true)}
                        startIcon={<LocalPrintshopIcon />}
                        sx={{ 
                            flex: 1,
                            minWidth: 160,
                            bgcolor: '#059669', 
                            color: '#ffffff',
                            fontWeight: 900, 
                            py: 1.3, 
                            borderRadius: 3,
                            fontSize: '0.95rem',
                            '&:hover': { bgcolor: '#047857' }
                        }}
                    >
                        🖨️ {isMr ? 'पावती प्रिंट व सेटल करा' : 'Print & Settle'}
                    </Button>

                    <Button
                        variant="outlined"
                        size="large"
                        onClick={() => handleConfirmSettle(false)}
                        startIcon={<CheckCircleIcon />}
                        sx={{ 
                            flex: 1,
                            minWidth: 140,
                            borderColor: '#059669', 
                            color: '#059669',
                            fontWeight: 900, 
                            py: 1.3, 
                            borderRadius: 3,
                            fontSize: '0.95rem',
                            '&:hover': { bgcolor: '#ecfdf5', borderColor: '#047857' }
                        }}
                    >
                        ✅ {isMr ? 'फक्त सेटल करा' : 'Settle Only'}
                    </Button>

                    <Button
                        variant="text"
                        onClick={() => setCheckoutModalOpen(false)}
                        sx={{ fontWeight: 800, color: '#64748b' }}
                    >
                        {isMr ? 'रद्द करा' : 'Cancel'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* OWNER BULK CLEAR OCCUPIED TABLES MODAL */}
            <Dialog
                open={bulkClearModalOpen}
                onClose={() => setBulkClearModalOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 4, p: 0.5 } }}
            >
                <DialogTitle sx={{ fontWeight: 900, bgcolor: '#dc2626', color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DeleteSweepIcon /> {isMr ? 'पेंडिंग टेबल्स रिसेट / मोकळे करा' : 'Reset / Clear Occupied Tables'}
                    </Box>
                    <IconButton size="small" onClick={() => setBulkClearModalOpen(false)} sx={{ color: '#ffffff' }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ p: 2.5, bgcolor: '#f8fafc' }}>
                    <Typography variant="body2" sx={{ color: '#334155', fontWeight: 700, mb: 2 }}>
                        {isMr 
                            ? 'खालीलपैकी जी टेबल्स मोकळी (Vacant) करायची आहेत ती निवडा. एका क्लिकवर ही टेबल्स आणि त्यांच्या जुन्या ऑर्डर्स रिसेट होतील:'
                            : 'Select the occupied/pending tables you want to reset to vacant:'}
                    </Typography>

                    {/* Master Select All Checkbox */}
                    {(() => {
                        const pendingTables = diningTables.filter(t => t.status === 'occupied' || t.status === 'billed');
                        const allSelected = pendingTables.length > 0 && pendingTables.every(t => selectedTableIdsForClear.includes(t.id));
                        
                        return (
                            <Box sx={{ mb: 1, px: 1, bgcolor: '#ffffff', p: 1, borderRadius: 2, border: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={allSelected}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setSelectedTableIdsForClear(pendingTables.map(t => t.id));
                                                } else {
                                                    setSelectedTableIdsForClear([]);
                                                }
                                            }}
                                            color="error"
                                        />
                                    }
                                    label={<Typography sx={{ fontWeight: 900 }}>{isMr ? 'सर्व टेबल्स निवडा (Select All)' : 'Select All Tables'}</Typography>}
                                />
                                <Chip label={`${selectedTableIdsForClear.length} / ${pendingTables.length} निवडले`} size="small" color="error" sx={{ fontWeight: 800 }} />
                            </Box>
                        );
                    })()}

                    <Box sx={{ maxHeight: 300, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {diningTables
                            .filter(t => t.status === 'occupied' || t.status === 'billed')
                            .map(t => {
                                const order = t.activeOrderId ? (orders || []).find(o => o.id === t.activeOrderId) : null;
                                const isChecked = selectedTableIdsForClear.includes(t.id);
                                const count = order ? (order.items || []).reduce((s, i) => s + (Number(i.quantity) || 1), 0) : 0;
                                const total = order ? Number(order.total || 0).toFixed(0) : 0;

                                return (
                                    <Box 
                                        key={t.id}
                                        onClick={() => {
                                            setSelectedTableIdsForClear(prev => 
                                                prev.includes(t.id) ? prev.filter(id => id !== t.id) : [...prev, t.id]
                                            );
                                        }}
                                        sx={{ 
                                            p: 1.5, 
                                            bgcolor: isChecked ? '#fef2f2' : '#ffffff', 
                                            border: isChecked ? '2px solid #dc2626' : '1px solid #cbd5e1', 
                                            borderRadius: 2.5,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Checkbox checked={isChecked} color="error" />
                                            <Box>
                                                <Typography sx={{ fontWeight: 900, color: '#0f172a' }}>
                                                    🍽️ Table {t.number}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>
                                                    {t.status === 'billed' ? '🟡 Billed' : '🔴 Occupied'} • Order #{t.activeOrderId || 'N/A'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Box sx={{ textAlign: 'right' }}>
                                            <Typography sx={{ fontWeight: 900, color: '#dc2626' }}>
                                                ₹{total}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: '#64748b' }}>
                                                {count} Items
                                            </Typography>
                                        </Box>
                                    </Box>
                                );
                            })
                        }
                        {diningTables.filter(t => t.status === 'occupied' || t.status === 'billed').length === 0 && (
                            <Box sx={{ textAlign: 'center', py: 3, bgcolor: '#ffffff', borderRadius: 2 }}>
                                <Typography sx={{ color: '#16a34a', fontWeight: 800 }}>
                                    🎉 सध्या कोणतेही टेबल Occupied किंवा पेंडिंग नाही!
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </DialogContent>

                <DialogActions sx={{ p: 2.5, bgcolor: '#ffffff', display: 'flex', gap: 1.5 }}>
                    <Button
                        variant="contained"
                        color="error"
                        fullWidth
                        size="large"
                        startIcon={<DeleteSweepIcon />}
                        onClick={handleExecuteBulkClear}
                        disabled={selectedTableIdsForClear.length === 0}
                        sx={{ fontWeight: 900, py: 1.3, borderRadius: 3, textTransform: 'none', fontSize: '0.95rem' }}
                    >
                        ✅ {isMr ? 'निवडलेली सर्व टेबल्स मोकळी (Vacant) करा' : 'Reset Selected Tables to Vacant'}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={() => setBulkClearModalOpen(false)}
                        sx={{ fontWeight: 800, borderRadius: 3 }}
                    >
                        {isMr ? 'रद्द करा' : 'Cancel'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Cancel Order Confirmation Modal */}
            <Dialog
                open={cancelModalOpen}
                onClose={() => setCancelModalOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ fontWeight: 900, color: '#dc2626' }}>
                    ❌ {isMr ? 'ऑर्डर रद्द करा (Cancel Order)' : 'Cancel Order'}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ color: '#334155', fontWeight: 600, mb: 2 }}>
                        {isMr 
                            ? `तुम्हाला खात्री आहे का की टेबल ${selectedTable?.number} ची ऑर्डर रद्द करायची आहे?` 
                            : `Are you sure you want to cancel the order for Table ${selectedTable?.number}?`}
                    </Typography>
                    <TextField
                        fullWidth
                        label={isMr ? "रद्द करण्याचे कारण (Reason)" : "Cancellation Reason"}
                        size="small"
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={() => {
                            if (typeof cancelOrder === 'function') {
                                cancelOrder(cancelReason, selectedTable?.id, selectedTable?.activeOrderId);
                            }
                            setCancelModalOpen(false);
                            setSelectedTable(null);
                        }}
                        sx={{ fontWeight: 900, borderRadius: 2 }}
                    >
                        {isMr ? 'होय, रद्द करा' : 'Yes, Cancel'}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={() => setCancelModalOpen(false)}
                        sx={{ fontWeight: 800, borderRadius: 2 }}
                    >
                        {isMr ? 'मागे जा' : 'Back'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
